import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Stats.module.scss';
import Stat from './Stat';
import LastTransaction from './LastTransaction';
import TotalTransactions from './TotalTransactions';

import ChevronLeft from '../../assets/icons/Chevron_Left.svg';
import ChevronRight from '../../assets/icons/Chevron_Right.svg';
import Wallet from '../../assets/icons/Wallet.svg';
import TrendingUp from '../../assets/icons/Trending_Up.svg';
import TrendingDown from '../../assets/icons/Trending_Down.svg';
import BuildingBank from '../../assets/icons/Building_Bank.svg';
import Settings from '../../assets/icons/Settings_purple.svg';

function toMonthKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function addMonths(date, delta) {
  const d = new Date(date);
  d.setDate(1);
  d.setMonth(d.getMonth() + delta);
  return d;
}

function formatMonthRu(date) {
  return new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' })
    .format(date)
    .replace(/^./, (c) => c.toUpperCase());
}

function formatMoney(value) {
  return new Intl.NumberFormat('ru-RU').format(Math.round(value || 0));
}

function pickTxTitle(t) {
  const title = String(t?.title || '').trim();
  if (title && title !== 'Без описания') return title;

  const cat =
    String(t?.category_name || '').trim() ||
    String(t?.categoryTitle || '').trim() ||
    String(t?.category || '').trim();

  if (cat) return cat;

  return 'Без описания';
}

function formatTxDate(iso) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';

  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();

  const y = new Date(now);
  y.setDate(now.getDate() - 1);

  const isYesterday =
    d.getFullYear() === y.getFullYear() &&
    d.getMonth() === y.getMonth() &&
    d.getDate() === y.getDate();

  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');

  if (sameDay) return `Сегодня в ${hh}:${mm}`;
  if (isYesterday) return `Вчера в ${hh}:${mm}`;

  const dd = String(d.getDate()).padStart(2, '0');
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mo} в ${hh}:${mm}`;
}

function isFutureMonth(date) {
  const now = new Date();
  const a = new Date(date.getFullYear(), date.getMonth(), 1).getTime();
  const b = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  return a > b;
}

const FadingText = ({ text, className }) => {
  const textRef = useRef(null);
  const [isOverflow, setIsOverflow] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      setIsOverflow(el.scrollWidth > el.clientWidth);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  return (
    <p
      ref={textRef}
      className={`${className} ${isOverflow ? styles.last__type_faded : ''}`}
      title={text}>
      {text}
    </p>
  );
};

const Stats = ({ onMonthChange }) => {
  const navigate = useNavigate();

  const [monthDate, setMonthDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });

  const monthKey = useMemo(() => toMonthKey(monthDate), [monthDate]);
  const monthLabel = useMemo(() => formatMonthRu(monthDate), [monthDate]);

  const nextMonth = useMemo(() => addMonths(monthDate, 1), [monthDate]);
  const isNextDisabled = useMemo(() => isFutureMonth(nextMonth), [nextMonth]);

  const [stats, setStats] = useState({
    balance: 0,
    income: 0,
    expense: 0,
    avgNetPerDay: 0,
    transactionsCount: 0,
    lastTransaction: null,
    maxExpense: null,
  });

  useEffect(() => {
    onMonthChange?.(monthKey);
  }, [monthKey, onMonthChange]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`/api/stats/month?month=${monthKey}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Ошибка загрузки stats');

        if (!cancelled) {
          setStats({
            balance: data.balance || 0,
            income: data.income || 0,
            expense: data.expense || 0,
            avgNetPerDay: data.avgNetPerDay || 0,
            transactionsCount: data.transactionsCount || 0,
            lastTransaction: data.lastTransaction || null,
            maxExpense: data.maxExpense || null,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setStats({
            balance: 0,
            income: 0,
            expense: 0,
            avgNetPerDay: 0,
            transactionsCount: 0,
            lastTransaction: null,
            maxExpense: null,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [monthKey]);

  const goTransactions = (params) => {
    if (!params) {
      navigate('/transactions');
      return;
    }

    const sp = new URLSearchParams();
    if (params.type && params.type !== 'all') sp.set('type', params.type);
    if (params.period && params.period !== 'all')
      sp.set('period', params.period);

    const qs = sp.toString();
    navigate(qs ? `/transactions?${qs}` : '/transactions');
  };

  const statsData = [
    {
      title: 'Баланс',
      value: stats.balance,
      icon: Wallet,
      bgColor: '#C9B45833',
      onClick: () => goTransactions(null),
      info: (
        <div className={styles.info__content}>
          <div className={styles.info__text}>
            Разница между доходами и расходами за выбранный месяц.
          </div>

          <div className={styles.info__formula}>Доход - Расход</div>
        </div>
      ),
    },
    {
      title: 'Доход',
      value: stats.income,
      icon: TrendingUp,
      bgColor: '#5F8F6B33',
      onClick: () => goTransactions({ type: 'income' }),
    },
    {
      title: 'Расход',
      value: stats.expense,
      icon: TrendingDown,
      bgColor: '#A05A5A33',
      onClick: () => goTransactions({ type: 'expense' }),
    },
    {
      title: 'Свободные деньги',
      value: 0,
      icon: BuildingBank,
      bgColor: '#5A7FA033',
      info: (
        <div className={styles.info__content}>
          <div className={styles.info__text}>
            Сумма, которую можно потратить без вреда целям и обязательным
            расходам.
          </div>
        </div>
      ),
    },
    {
      title: 'Средне в день',
      value: stats.avgNetPerDay,
      icon: Settings,
      bgColor: '#7A6F8F33',
      info: (
        <div className={styles.info__content}>
          <div className={styles.info__text}>
            Среднее значение баланса в день
          </div>

          <div className={styles.info__formula}>
            Доход - Расход / Дней в месяце
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.stats}>
      <div className={styles.stats__header}>
        <h2 className={styles.stats__title}>Обзор</h2>

        <div className={styles.stats__buttons}>
          <button
            className={styles.stats__button}
            onClick={() => setMonthDate((d) => addMonths(d, -1))}>
            <img src={ChevronLeft} alt='' className={styles.stats__icon} />
          </button>

          <span className={styles.stats__month}>{monthLabel}</span>

          <button
            className={`${styles.stats__button} ${
              isNextDisabled ? styles.stats__buttonDisabled : ''
            }`}
            disabled={isNextDisabled}
            onClick={() => {
              if (isNextDisabled) return;
              setMonthDate((d) => addMonths(d, 1));
            }}>
            <img src={ChevronRight} alt='' className={styles.stats__icon} />
          </button>
        </div>
      </div>

      <div className={styles.stats__blocks}>
        {statsData.map((stat, index) => (
          <Stat
            key={index}
            title={stat.title}
            value={stat.value}
            icon={stat.icon}
            bgColor={stat.bgColor}
            onClick={stat.onClick}
            info={stat.info}
          />
        ))}

        <TotalTransactions
          title='Транзакций'
          value={stats.transactionsCount}
          icon={Settings}
          bgColor='#7A6F8F33'
        />

        <LastTransaction title='Последняя транзакция'>
          {stats.lastTransaction ? (
            <div
              className={`${styles.last__block} ${
                styles[stats.lastTransaction.type]
              }`}>
              <div className={styles.last__row}>
                <FadingText
                  text={pickTxTitle(stats.lastTransaction)}
                  className={styles.last__type}
                />

                <p
                  className={`${styles.last__value} ${
                    styles[stats.lastTransaction.type]
                  }`}>
                  <span className={styles.amountWithCurrency}>
                    {stats.lastTransaction.type === 'income' ? '+' : '-'}
                    {formatMoney(stats.lastTransaction.amount)} ₽
                  </span>
                </p>
              </div>

              <p className={styles.last__date}>
                {formatTxDate(stats.lastTransaction.created_at)}
              </p>
            </div>
          ) : (
            <div className={styles.last__empty}>Нет транзакций</div>
          )}
        </LastTransaction>

        <LastTransaction title='Максимальная трата'>
          {stats.maxExpense ? (
            <div className={`${styles.last__block} ${styles.expense}`}>
              <div className={styles.last__row}>
                <FadingText
                  text={pickTxTitle(stats.maxExpense)}
                  className={styles.last__type}
                />

                <p className={`${styles.last__value} ${styles.expense}`}>
                  -{formatMoney(stats.maxExpense.amount)} ₽
                </p>
              </div>

              <p className={styles.last__date}>
                {formatTxDate(stats.maxExpense.created_at)}
              </p>
            </div>
          ) : (
            <div className={styles.last__empty}>Нет расходов</div>
          )}
        </LastTransaction>
      </div>
    </div>
  );
};

export default Stats;
